import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import {
    SalaryPayment,
    SalaryPayments,
    SalaryCalculation,
    CalculateSalaryPayload,
    ProcessSalaryPayload,
    PaySalaryPayload,
    FetchSalariesParams,
    SalaryDeleteResponse,
    CalculateDailyWagePayload,
    ProcessDailyWagePayload,
    DailyWageCalculation
} from "../../utils/salary.types";

/**
 * Calculate Salary (Preview) - Monthly Salary
 */
export const calculateSalary = createAsyncThunk(
    'salary/calculateSalary',
    async (payload: CalculateSalaryPayload): Promise<{ data: SalaryCalculation }> => {
        const response = await axios.post(`/api/v1/salary/calculate/${payload.employeeId}`, {
            month: payload.month,
            workingDays: payload.workingDays,
            presentDays: payload.presentDays,
            basicSalary: payload.basicSalary,  // New: Basic salary from admin
            hra: payload.hra,  // New: HRA from admin
            salaryAmount: payload.salaryAmount,  // Deprecated but kept for compatibility
            additionalEarnings: payload.additionalEarnings,  // New: Additional earnings array
            taxConfig: payload.taxConfig
        });
        return response.data;
    }
);

/**
 * Calculate Bulk Salary - All employees for a month
 */
export const calculateBulkSalary = createAsyncThunk(
    'salary/calculateBulkSalary',
    async (payload: { month: string; workingDays?: number }): Promise<{ data: SalaryCalculation[] }> => {
        const response = await axios.post(`/api/v1/salary/calculate-bulk`, payload);
        return response.data;
    }
);

/**
 * Process Salary - Create and save salary payment
 */
export const processSalary = createAsyncThunk(
    'salary/processSalary',
    async (payload: ProcessSalaryPayload): Promise<{ data: SalaryPayment }> => {
        // Map frontend payload to backend expected format
        const backendPayload = {
            employee: payload.employeeId,  // Backend expects 'employee' not 'employeeId'
            month: payload.month,
            workingDays: payload.workingDays,
            presentDays: payload.presentDays,
            basicSalary: payload.basicSalary,  // New: Basic salary from admin
            hra: payload.hra,  // New: HRA from admin
            salaryAmount: payload.salaryAmount,  // Include manually entered salary amount (deprecated)
            additionalEarnings: payload.additionalEarnings,  // New: Additional earnings array
            costCentre: payload.costCentre,  // Cost centre for expense allocation
            taxConfig: payload.taxConfig,  // Include tax configuration
            paymentStatus: payload.paymentOptions?.paymentStatus || 'PENDING',
            paymentMode: payload.paymentOptions?.paymentMode || 'NEFT',
            fromBankAccount: payload.paymentOptions?.fromBankAccount,
            remarks: payload.paymentOptions?.remarks,
        };

        const response = await axios.post(`/api/v1/salary/process`, backendPayload);
        return response.data;
    }
);

/**
 * Fetch Salaries - Get salary list with filters
 */
export const fetchSalaries = createAsyncThunk(
    'salary/fetchSalaries',
    async (params: FetchSalariesParams): Promise<SalaryPayments> => {
        let queryParams = `accountId=${params.accountId}`;

        if (params.employee) queryParams += `&employee=${params.employee}`;
        if (params.department) queryParams += `&department=${params.department}`;
        if (params.status) queryParams += `&status=${params.status}`;
        if (params.month) queryParams += `&month=${params.month}`;
        if (params.year) queryParams += `&year=${params.year}`;
        if (params.startDate) queryParams += `&startDate=${params.startDate}`;
        if (params.endDate) queryParams += `&endDate=${params.endDate}`;

        const response = await axios.get(`/api/v1/salary?${queryParams}`);
        return response.data;
    }
);

/**
 * Fetch Salary by ID
 */
export const fetchSalaryById = createAsyncThunk(
    'salary/fetchSalaryById',
    async (id: string): Promise<{ data: SalaryPayment }> => {
        const response = await axios.get(`/api/v1/salary/${id}`);
        return response.data;
    }
);

/**
 * Update Salary Payment
 */
export const updateSalary = createAsyncThunk(
    'salary/updateSalary',
    async ({ id, payload }: { id: string; payload: any }): Promise<{ data: SalaryPayment }> => {
        const response = await axios.put(`/api/v1/salary/${id}`, payload);
        return response.data;
    }
);

/**
 * Pay Salary - Mark as paid and create payment journal entry
 */
export const paySalary = createAsyncThunk(
    'salary/paySalary',
    async (payload: PaySalaryPayload): Promise<{ data: SalaryPayment }> => {
        const { id, ...paymentData } = payload;
        const response = await axios.post(`/api/v1/salary/${id}/pay`, paymentData);
        return response.data;
    }
);

/**
 * Delete Salary Payment
 */
export const deleteSalary = createAsyncThunk(
    'salary/deleteSalary',
    async (id: string): Promise<SalaryDeleteResponse> => {
        const response = await axios.delete(`/api/v1/salary/${id}`);
        return response.data;
    }
);

// ===========================
// DAILY WAGE THUNKS
// ===========================

/**
 * Calculate Daily Wage (Preview)
 */
export const calculateDailyWage = createAsyncThunk(
    'salary/calculateDailyWage',
    async (payload: CalculateDailyWagePayload): Promise<{ data: DailyWageCalculation }> => {
        const response = await axios.post(`/api/v1/daily-wage/calculate`, payload);
        return response.data;
    }
);

/**
 * Process Daily Wage - Create and save daily wage payment
 */
export const processDailyWage = createAsyncThunk(
    'salary/processDailyWage',
    async (payload: ProcessDailyWagePayload): Promise<{ data: SalaryPayment }> => {
        const response = await axios.post(`/api/v1/daily-wage/process`, payload);
        return response.data;
    }
);

/**
 * Fetch Daily Wages - Get daily wage list with filters
 */
export const fetchDailyWages = createAsyncThunk(
    'salary/fetchDailyWages',
    async (params: FetchSalariesParams): Promise<SalaryPayments> => {
        let queryParams = `accountId=${params.accountId}`;

        if (params.employee) queryParams += `&employee=${params.employee}`;
        if (params.department) queryParams += `&department=${params.department}`;
        if (params.status) queryParams += `&status=${params.status}`;
        if (params.startDate) queryParams += `&startDate=${params.startDate}`;
        if (params.endDate) queryParams += `&endDate=${params.endDate}`;

        const response = await axios.get(`/api/v1/daily-wage?${queryParams}`);
        return response.data;
    }
);

/**
 * Fetch Daily Wage by ID
 */
export const fetchDailyWageById = createAsyncThunk(
    'salary/fetchDailyWageById',
    async (id: string): Promise<{ data: SalaryPayment }> => {
        const response = await axios.get(`/api/v1/daily-wage/${id}`);
        return response.data;
    }
);

/**
 * Pay Daily Wage
 */
export const payDailyWage = createAsyncThunk(
    'salary/payDailyWage',
    async (payload: PaySalaryPayload): Promise<{ data: SalaryPayment }> => {
        const { id, ...paymentData } = payload;
        const response = await axios.post(`/api/v1/daily-wage/${id}/pay`, paymentData);
        return response.data;
    }
);

/**
 * Delete Daily Wage Payment
 */
export const deleteDailyWage = createAsyncThunk(
    'salary/deleteDailyWage',
    async (id: string): Promise<SalaryDeleteResponse> => {
        const response = await axios.delete(`/api/v1/daily-wage/${id}`);
        return response.data;
    }
);
